import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { saveAs } from "file-saver";

// Format date for filename
const formatDateForFilename = () => {
  const now = new Date();
  return now.toLocaleDateString().split("T")[0]; // YYYY-MM-DD format
};

// Export to Excel
export const exportToExcel = (data, filters) => {
  try {
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    // Prepare data for Excel
    const excelData = data.map((item) => ({
      Month: item.monthName || monthNames[item.month - 1],
      Year: item.year || filters.year,
      "Total Bookings": item.totalBookings || 0,
      Completed: item.totalCompleted || 0,
      Cancelled: item.totalCancelled || 0,
      Pending: item.totalPending || 0,
      "Total Guests": item.totalGuest || 0,
      "Completion Rate (%)":
        item.totalBookings > 0
          ? Math.round((item.totalCompleted / item.totalBookings) * 100)
          : 0,
    }));

    // Add summary row
    const totals = data.reduce(
      (acc, item) => ({
        totalBookings: acc.totalBookings + (item.totalBookings || 0),
        totalCompleted: acc.totalCompleted + (item.totalCompleted || 0),
        totalCancelled: acc.totalCancelled + (item.totalCancelled || 0),
        totalPending: acc.totalPending + (item.totalPending || 0),
        totalGuest: acc.totalGuest + (item.totalGuest || 0),
      }),
      {
        totalBookings: 0,
        totalCompleted: 0,
        totalCancelled: 0,
        totalPending: 0,
        totalGuest: 0,
      }
    );

    excelData.push({
      Month: "TOTAL",
      Year: "",
      "Total Bookings": totals.totalBookings,
      Completed: totals.totalCompleted,
      Cancelled: totals.totalCancelled,
      Pending: totals.totalPending,
      "Total Guests": totals.totalGuest,
      "Completion Rate (%)":
        totals.totalBookings > 0
          ? Math.round((totals.totalCompleted / totals.totalBookings) * 100)
          : 0,
    });

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Set column widths
    const colWidths = [
      { wch: 12 }, // Month
      { wch: 8 }, // Year
      { wch: 15 }, // Total Bookings
      { wch: 12 }, // Completed
      { wch: 12 }, // Cancelled
      { wch: 10 }, // Pending
      { wch: 12 }, // Total Guests
      { wch: 18 }, // Completion Rate
    ];
    ws["!cols"] = colWidths;

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Booking Report");

    // Generate filename
    const period = filters.month
      ? `${monthNames[filters.month - 1]}_${filters.year}`
      : `${filters.year}`;
    const filename = `booking_report_${period}_${formatDateForFilename()}.xlsx`;

    // Save file
    XLSX.writeFile(wb, filename);

    return { success: true, filename };
  } catch (error) {
    console.error("Error exporting to Excel:", error);
    return { success: false, error: error.message };
  }
};

// Export to CSV
export const exportToCSV = (data, filters) => {
  try {
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    // Prepare CSV headers
    const headers = [
      "Month",
      "Year",
      "Total Bookings",
      "Completed",
      "Cancelled",
      "Pending",
      "Total Guests",
      "Completion Rate (%)",
    ];

    // Prepare CSV rows
    const rows = data.map((item) => [
      item.monthName || monthNames[item.month - 1],
      item.year || filters.year,
      item.totalBookings || 0,
      item.totalCompleted || 0,
      item.totalCancelled || 0,
      item.totalPending || 0,
      item.totalGuest || 0,
      item.totalBookings > 0
        ? Math.round((item.totalCompleted / item.totalBookings) * 100)
        : 0,
    ]);

    // Add totals row
    const totals = data.reduce(
      (acc, item) => ({
        totalBookings: acc.totalBookings + (item.totalBookings || 0),
        totalCompleted: acc.totalCompleted + (item.totalCompleted || 0),
        totalCancelled: acc.totalCancelled + (item.totalCancelled || 0),
        totalPending: acc.totalPending + (item.totalPending || 0),
        totalGuest: acc.totalGuest + (item.totalGuest || 0),
      }),
      {
        totalBookings: 0,
        totalCompleted: 0,
        totalCancelled: 0,
        totalPending: 0,
        totalGuest: 0,
      }
    );

    rows.push([
      "TOTAL",
      "",
      totals.totalBookings,
      totals.totalCompleted,
      totals.totalCancelled,
      totals.totalPending,
      totals.totalGuest,
      totals.totalBookings > 0
        ? Math.round((totals.totalCompleted / totals.totalBookings) * 100)
        : 0,
    ]);

    // Convert to CSV string
    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    // Create and download file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const period = filters.month
      ? `${monthNames[filters.month - 1]}_${filters.year}`
      : `${filters.year}`;
    const filename = `booking_report_${period}_${formatDateForFilename()}.csv`;

    saveAs(blob, filename);

    return { success: true, filename };
  } catch (error) {
    console.error("Error exporting to CSV:", error);
    return { success: false, error: error.message };
  }
};

// Export to PDF
export const exportToPDF = (data, filters) => {
  try {
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    // Create new PDF document
    const doc = new jsPDF();

    // Add title
    doc.setFontSize(20);
    doc.setFont("helvetica", "bold");
    doc.text("Booking Report", 20, 25);

    // Add period info
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    const period = filters.month
      ? `${monthNames[filters.month - 1]} ${filters.year}`
      : `Year ${filters.year}`;
    doc.text(`Period: ${period}`, 20, 35);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 45);

    // Prepare table data
    const tableData = data.map((item) => [
      item.monthName || monthNames[item.month - 1],
      item.year || filters.year,
      item.totalBookings || 0,
      item.totalCompleted || 0,
      item.totalCancelled || 0,
      item.totalPending || 0,
      item.totalGuest || 0,
      item.totalBookings > 0
        ? `${Math.round((item.totalCompleted / item.totalBookings) * 100)}%`
        : "0%",
    ]);

    // Add totals row
    const totals = data.reduce(
      (acc, item) => ({
        totalBookings: acc.totalBookings + (item.totalBookings || 0),
        totalCompleted: acc.totalCompleted + (item.totalCompleted || 0),
        totalCancelled: acc.totalCancelled + (item.totalCancelled || 0),
        totalPending: acc.totalPending + (item.totalPending || 0),
        totalGuest: acc.totalGuest + (item.totalGuest || 0),
      }),
      {
        totalBookings: 0,
        totalCompleted: 0,
        totalCancelled: 0,
        totalPending: 0,
        totalGuest: 0,
      }
    );

    tableData.push([
      "TOTAL",
      "",
      totals.totalBookings,
      totals.totalCompleted,
      totals.totalCancelled,
      totals.totalPending,
      totals.totalGuest,
      totals.totalBookings > 0
        ? `${Math.round((totals.totalCompleted / totals.totalBookings) * 100)}%`
        : "0%",
    ]);

    autoTable(doc, {
      head: [
        [
          "Month",
          "Year",
          "Total Bookings",
          "Completed",
          "Cancelled",
          "Pending",
          "Total Guests",
          "Completion Rate",
        ],
      ],
      body: tableData,
      startY: 55,
      styles: {
        fontSize: 9,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: "bold",
      },
      bodyStyles: {
        textColor: 50,
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },
      didParseCell: function (data) {
        // Highlight the TOTAL row
        if (data.row.index === tableData.length - 1) {
          data.cell.styles.fillColor = [52, 152, 219];
          data.cell.styles.textColor = 255;
          data.cell.styles.fontStyle = "bold";
        }
      },
    });

    // Add summary section
    const finalY = doc.lastAutoTable.finalY + 20;
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Summary", 20, finalY);

    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`Total Bookings: ${totals.totalBookings}`, 20, finalY + 10);
    doc.text(`Completed: ${totals.totalCompleted}`, 20, finalY + 20);
    doc.text(`Cancelled: ${totals.totalCancelled}`, 20, finalY + 30);
    doc.text(`Pending: ${totals.totalPending}`, 20, finalY + 40);
    doc.text(`Total Guests: ${totals.totalGuest}`, 20, finalY + 50);
    doc.text(
      `Overall Completion Rate: ${
        totals.totalBookings > 0
          ? Math.round((totals.totalCompleted / totals.totalBookings) * 100)
          : 0
      }%`,
      20,
      finalY + 60
    );

    // Save PDF
    const filename = `booking_report_${period.replace(
      " ",
      "_"
    )}_${formatDateForFilename()}.pdf`;
    doc.save(filename);

    return { success: true, filename };
  } catch (error) {
    console.error("Error exporting to PDF:", error);
    return { success: false, error: error.message };
  }
};
